import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getTicketById,
} from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import { COMMERCIAL_INTELLIGENCE_PATHS } from "../../helpers/MenuPathHelper";

import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Tag, 
  Space, 
  Table, 
  Divider,
  Statistic,
  Badge,
  Skeleton
} from "antd";
import { 
  UserOutlined,
  ShopOutlined,
  DollarOutlined,
  CreditCardOutlined
} from "@ant-design/icons";
import { RxCardStack } from "react-icons/rx";

import styles from "./ViewTicket.module.scss";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import NotFound from "../notfound/NotFound";
import CustomBreadcrumb from "../../components/breadcrumb/Breadcrumb";
import moment from "moment";

const ViewTicket = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<any>();

  const { id } = useParams();

  const getBasePath = () => {
    const path = location.pathname;
    if (path.startsWith('/commercial-intelligence')) return '/commercial-intelligence';
    if (path.startsWith('/fidelity')) return '/fidelity';
    if (path.startsWith('/promotions')) return '/promotions';
    return '/promotions';
  };

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_TICKETS)) {
      handleFetchTicket();
    }
  }, []);

  const handleFetchTicket = async () => {
    setLoading(true);

    try {
    let response = await getTicketById(id!);
    setTicket(response);
    } catch (error) {
      console.error('Error fetching ticket:', error);
      setTicket(null);
    } finally {
    setLoading(false);
    }
  };

  const skusColumns = [
    {
      title: t("Common-Name"),
      key: "sku",
      render: (data: any) => (
        <Typography.Link 
          href={`/admin/articles/${data.sku.articleId}/view`}
          strong
        >
          {data.sku.name}
        </Typography.Link>
      ),
    },
    {
      title: t("Common-UnitPrice"),
      dataIndex: "unitPrice",
      key: "unitPrice",
      render: (price: number) => (
        <Typography.Text strong>${price?.toLocaleString('es-ES')}</Typography.Text>
      ),
    },
    {
      title: t("Common-Quantity"),
      dataIndex: "quantity",
      key: "quantity",
      render: (quantity: number) => (
        <Badge count={quantity} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
  ];

  const promotionsColumns = [
    {
      title: t("Common-Name"),
      key: "name",
      render: (data: any) => (
        <Typography.Link
          href={`${COMMERCIAL_INTELLIGENCE_PATHS.promotions}/${data.id}/view`}
        >
          {data.name}
        </Typography.Link>
      ),
    },
    {
      title: t("Common-Type"),
      key: "type",
      render: (data: any) => (
        <Tag color="blue">{data.type.description_es}</Tag>
      ),
    },
    {
      title: t("Common-DistributeLines"),
      key: "distributeLines",
      render: (data: any) => (
        <Tag color={data.distributeLines ? "green" : "default"}>
          {data.distributeLines ? t("Common-Yes") : t("Common-No")}
        </Tag>
      ),
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'green';
      case 'pending': return 'orange';
      case 'cancelled': return 'red';
      default: return 'blue';
    }
  };

  const getStatusText = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed': return t('Common-Completed');
      case 'pending': return t('Common-Pending');
      case 'cancelled': return t('Common-Cancelled');
      default: return t('Common-Processed');
    }
  };

  const TicketSkeleton = () => (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxCardStack />,
                label: t("Common-Tickets"),
                path: `${getBasePath()}/tickets`,
                search: location.search
              },
              {
                label: t("Common-Loading"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={8}>
            <Card className={styles.infoCard}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card className={styles.infoCard}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Card className={styles.infoCard}>
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card className={styles.productsCard}>
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          </Col>
          <Col xs={24} lg={8}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card className={styles.promotionsCard} size="small">
                <Skeleton active paragraph={{ rows: 3 }} />
              </Card>
              <Card className={styles.paymentCard} size="small">
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );


  return loading ? (
    <TicketSkeleton />
  ) : ticket === null ? (
    <NotFound />
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxCardStack />,
                label: t("Common-Tickets"),
                path: `${getBasePath()}/tickets`,
                search: location.search
              },
              {
                label: `${t("Common-Ticket")} #${ticket.code}`,
                isCurrentPage: true
              }
            ]}
          />
        </div>
      </div>

      <div className={styles.contentWrapper}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <Card 
                  title={
                    <Space>
                      <UserOutlined />
                      <span>{t("Common-ClientInfo")}</span>
                    </Space>
                  }
                  className={styles.infoCard}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div className={styles.infoItem}>
                      <Typography.Text type="secondary">{t("Common-ClientRut")}</Typography.Text>
                      <Typography.Text strong>{ticket.clientRut}</Typography.Text>
                    </div>
                    <div className={styles.infoItem}>
                      <Typography.Text type="secondary">{t("Common-Client")}</Typography.Text>
                      {ticket.client?.id ? (
                        <Typography.Link 
                          href={`${getBasePath()}/clients/${ticket.client.id}/view`}
                          strong
                        >
                          {ticket.client.idNumber}
                        </Typography.Link>
                      ) : (
                        <Typography.Text strong>{t("Common-NotAvailable")}</Typography.Text>
                      )}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col xs={24}>
                <Card 
                  title={
                    <Space>
                      <ShopOutlined />
                      <span>{t("Common-SalePoint")}</span>
                    </Space>
                  }
                  className={styles.infoCard}
                >
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div className={styles.infoItem}>
                      <Typography.Text type="secondary">{t("Common-SalePoint")}</Typography.Text>
                      <Typography.Text strong>{ticket.salePoint?.name}</Typography.Text>
                    </div>
                    <div className={styles.infoItem}>
                      <Typography.Text type="secondary">{t("Common-BranchOffice")}</Typography.Text>
                      <Typography.Link 
                        href={`/admin/branchoffices/${ticket.salePoint?.branchOffice?.id}/view`}
                        strong
                      >
                        {ticket.salePoint?.branchOffice?.name}
                      </Typography.Link>
                    </div>
                    <div className={styles.infoItem}>
                      <Typography.Text type="secondary">{t("Common-Channel")}</Typography.Text>
                      <Typography.Text strong>{ticket.channel?.type?.descriptionEs}</Typography.Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Col>

          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <DollarOutlined />
                  <span>Resumen del ticket</span>
                </Space>
              }
              className={styles.infoCard}
            >
              <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
                <div className={styles.infoItem}>
                  <Typography.Text type="secondary">{t("Common-DocumentType")}</Typography.Text>
                  <Typography.Text strong>{ticket.documentType?.description_es}</Typography.Text>
                </div>
                <div className={styles.infoItem}>
                  <Typography.Text type="secondary">{t("Common-Date")}</Typography.Text>
                  <Typography.Text strong>
                    {moment(ticket.date).format('DD/MM/YYYY HH:mm')}
                  </Typography.Text>
                </div>
              </Space>
              <Divider />
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title={t("Common-SubTotal")}
                    value={ticket.subtotal}
                    precision={2}
                    prefix="$"
                    formatter={(value) => value?.toLocaleString('es-ES')}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={t("Common-Iva")}
                    value={ticket.iva}
                    precision={2}
                    prefix="$"
                    formatter={(value) => value?.toLocaleString('es-ES')}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title={t("Common-Total")}
                    value={ticket.total}
                    precision={2}
                    prefix="$"
                    formatter={(value) => value?.toLocaleString('es-ES')}
                    valueStyle={{ fontSize: '18px', color: '#1890ff' }}
                  />
                </Col>
              </Row>
              <Divider />
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title={t("Common-AccumulatePoints")}
                    value={ticket.accumulatePoints}
                    formatter={(value) => value?.toLocaleString('es-ES')}
                    valueStyle={{ fontSize: '16px', color: '#52c41a' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t("Common-ConsumedPoints")}
                    value={ticket.consumedPoints}
                    formatter={(value) => value?.toLocaleString('es-ES')}
                    valueStyle={{ fontSize: '16px', color: '#fa8c16' }}
                  />
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
          <Col xs={24} lg={16}>
            <Card 
              title={
                <Space>
                  <span>{t("Common-Articles")} ({ticket?.skusWithUnitPrice?.length || 0})</span>
                </Space>
              }
              className={styles.productsCard}
            >
          <Table
            size="small"
                className={styles.modernTable}
            loading={loading}
            columns={skusColumns}
            dataSource={ticket?.skusWithUnitPrice}
            rowKey={(record) => record.id}
            pagination={false}
                locale={{
                  emptyText: (
                    <div className={styles.emptyState}>
                      <Typography.Text type="secondary">{t("Common-NoArticlesInTicket")}</Typography.Text>
                    </div>
                  )
                }}
              />
            </Card>
          </Col>

          <Col xs={24} lg={8}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Card 
                title={
                  <Space>
                    <span>{t("Common-Promotions")} ({ticket?.promotions?.length || 0})</span>
                  </Space>
                }
                className={styles.promotionsCard}
                size="small"
              >
                {ticket?.promotions?.length > 0 ? (
          <Table
            size="small"
                    className={styles.modernTable}
            columns={promotionsColumns}
            dataSource={ticket?.promotions}
            pagination={false}
                    showHeader={false}
                  />
                ) : (
                  <Typography.Text type="secondary">{t("Common-NoPromotionsApplied")}</Typography.Text>
                )}
              </Card>

              <Card 
                title={
                  <Space>
                    <CreditCardOutlined />
                    <span>{t("Common-PaymentMethods")}</span>
                  </Space>
                }
                className={styles.paymentCard}
                size="small"
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {ticket?.acceptedPaymentMethods?.map((payment: any, index: number) => (
                    <div key={index} className={styles.paymentMethod}>
                      <Badge status="success" />
                      <Typography.Text>{payment.name}</Typography.Text>
        </div>
                  ))}
                </Space>
              </Card>
            </Space>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ViewTicket;
